import {
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  AnalyticsDashboardsPageDocument,
  UpdateOneAnalyticsDashboardDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { DashboardNode } from 'modules/ai_reports/lib/types'

type Props = {
  itemToEdit: DashboardNode | null
  setItemToEdit: (item: DashboardNode | null) => void
}

const EditDashboardDialog = ({ itemToEdit, setItemToEdit }: Props) => {
  const { company } = useCurrentSession()

  const [updateDashboard] = useMutation(UpdateOneAnalyticsDashboardDocument, {
    extractorFn: (data) => data.oneAnalyticsMutations.updateOneanalyticsDashboard,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const schema = z.object({
    title: f0FormField(z.string().trim().min(1), {
      label: i18n.t('ai_reports.dashboards.edit.labels.title'),
    }),
    description: f0FormField(z.string().trim().optional(), {
      label: i18n.t('ai_reports.dashboards.edit.labels.description'),
      fieldType: 'textarea',
    }),
  })

  const handleClose = () => setItemToEdit(null)

  const formDefinition = useF0FormDefinition({
    name: 'edit-dashboard',
    schema,
    defaultValues: {
      title: itemToEdit?.title ?? '',
      description: itemToEdit?.description ?? '',
    },
    onSubmit: async ({ data }) => {
      if (!itemToEdit) return { success: false, errors: {} }

      const result = await updateDashboard({
        variables: {
          id: itemToEdit.id,
          companyId: company.id,
          title: data.title,
          description: data.description?.trim() ? data.description : null,
        },
        refetchQueries: [AnalyticsDashboardsPageDocument],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(result, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('ai_reports.dashboards.edit.notifications.error'),
        })
        return { success: false, errors: fieldErrors }
      }

      app.setFlash({
        type: 'correct',
        message: i18n.t('ai_reports.dashboards.edit.notifications.success'),
      })
      handleClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  if (!itemToEdit) return null

  return (
    <F0Dialog
      isOpen
      onClose={handleClose}
      title={i18n.t('ai_reports.dashboards.edit.title')}
      description={i18n.t('ai_reports.dashboards.edit.description')}
      primaryAction={{
        label: i18n.t('ai_reports.dashboards.edit.buttons.save'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('ai_reports.dashboards.edit.buttons.cancel'),
        onClick: handleClose,
      }}
    >
      <F0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}

export default EditDashboardDialog
